import React, { useEffect, useRef, useState } from 'react'
import { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Icon } from '../../atoms/Icon'
import { IconButton } from '../../atoms/IconButton'
import { Text } from '../../atoms/Text'
import { Button } from '../../atoms/Button'
import {
  ResizableDetailPanel,
  DetailPanelHeader,
  DetailPanelContent,
  DetailPanelFooter,
} from '../ResizableDetailPanel'
import { TransactionDetailProps, Transaction } from './TransactionDetail.types'
import { TransactionDetailContent } from './TransactionDetailContent'
import {
  DetailHeaderInfo,
  HeaderActions,
  LandDetailBackButton,
} from './TransactionDetail.styles'

export const TransactionDetail: React.FC<TransactionDetailProps> = ({
  isOpen,
  onClose,
  transactionId,
  loadTransaction,
  isSaved,
  onToggleSave,
  contained = false,
  disableResize = false,
  printMode = false,
  onAddToDeal,
  // Land integration
  resolvedLandsMap,
  landsLoading,
  onViewLand,
  landDetailContent,
  landDetailOpen = false,
  onLandDetailClose,
  onTransactionLoaded,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [transaction, setTransaction] = useState<Transaction | null>(null)
  const [loading, setLoading] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!transactionId || !loadTransaction) {
      setTransaction(null)
      onTransactionLoaded?.(null)
      return
    }

    const loadTransactionData = async () => {
      setLoading(true)
      try {
        const data = await loadTransaction(transactionId)
        setTransaction(data)
        onTransactionLoaded?.(data)
      } catch (error) {
        console.error('Error loading transaction:', error)
        setTransaction(null)
        onTransactionLoaded?.(null)
      } finally {
        setLoading(false)
      }
    }

    loadTransactionData()
  }, [transactionId, loadTransaction])

  // Scroll to top when switching between transaction and land views
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0
    }
  }, [landDetailOpen])

  // In print mode, render just the content without the panel wrapper
  if (printMode) {
    return (
      <TransactionDetailContent
        transaction={transaction}
        loading={loading}
        printMode
        resolvedLandsMap={resolvedLandsMap}
        landsLoading={landsLoading}
        onViewLand={onViewLand}
      />
    )
  }

  return (
    <ResizableDetailPanel
      isOpen={isOpen}
      onClose={onClose}
      storageKey="transaction_detail_panel_width"
      defaultWidth="900px"
      minWidth={600}
      maxWidthPercentage={0.8}
      contained={contained}
      disableResize={disableResize}
    >
      {landDetailOpen && landDetailContent ? (
        <>
          <DetailPanelHeader onClose={onClose}>
            <LandDetailBackButton onClick={onLandDetailClose}>
              <Icon name="arrow-left" size="small" />
              {t('transactions.detailPanel.labels.backToTransaction')}
            </LandDetailBackButton>
          </DetailPanelHeader>
          <DetailPanelContent ref={contentRef}>
            {landDetailContent}
          </DetailPanelContent>
        </>
      ) : (
        <>
          <DetailPanelHeader onClose={onClose}>
            <DetailHeaderInfo>
              <Text variant="caption" color="secondary">
                {t('transactions.detailPanel.type')}
              </Text>
            </DetailHeaderInfo>
            <HeaderActions>
              {onToggleSave && (
                <IconButton
                  onClick={onToggleSave}
                  size="small"
                  variant={isSaved ? 'solid' : 'default'}
                  title={
                    isSaved
                      ? t('transactions.detailPanel.removeFromSaved')
                      : t('transactions.detailPanel.saveTransaction')
                  }
                  aria-label={
                    isSaved
                      ? t('transactions.detailPanel.removeFromSaved')
                      : t('transactions.detailPanel.saveTransaction')
                  }
                >
                  <Icon
                    name="bookmark"
                    size="medium"
                    color={isSaved ? theme.colors.primary : undefined}
                    fill={isSaved ? theme.colors.primary : 'transparent'}
                  />
                </IconButton>
              )}
            </HeaderActions>
          </DetailPanelHeader>

          <DetailPanelContent>
            <TransactionDetailContent
              transaction={transaction}
              loading={loading}
              resolvedLandsMap={resolvedLandsMap}
              landsLoading={landsLoading}
              onViewLand={onViewLand}
            />
          </DetailPanelContent>

          {onAddToDeal && !printMode && (
            <DetailPanelFooter>
              <Button
                variant="secondary"
                onClick={() => transaction && onAddToDeal?.(transaction)}
              >
                <Icon name="git-branch" size="small" />
                Add to Deal
              </Button>
            </DetailPanelFooter>
          )}
        </>
      )}
    </ResizableDetailPanel>
  )
}
